import type { ShiftListItem, TeamShiftAssignment } from '@/domains/shifts';
import type { User } from '@/domains/users';
import { PageHeader } from '@/shared/components/PageHeader';
import { ALLOWED_TEXT_PATTERN, TEXT_VALIDATION_MSG } from '@/shared/lib/validation';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import {
  Clock,
  Loader2,
  Mail,
  Pencil,
  Search,
  Trash2,
  UserPlus,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { useState } from 'react';
import type { TeamDetail, UpdateTeamInput } from '../types/team';

interface TeamDetailViewProps {
  team: TeamDetail | null;
  isLoading?: boolean;
  onBack: () => void;
  allUsers?: User[];
  allShifts?: ShiftListItem[];
  assignedShifts?: TeamShiftAssignment[];
  onAddMembers?: (userIds: number[]) => Promise<void>;
  onRemoveMember?: (userId: number, newTeamLeadId?: number) => Promise<void>;
  onUpdateTeam?: (data: UpdateTeamInput) => Promise<void>;
  onAssignShift?: (shiftId: number, startDate: string, endDate: string) => Promise<void>;
  onRemoveShift?: (shiftId: number, startDate: string, endDate: string) => Promise<void>;
}

export function TeamDetailView({
  team,
  isLoading = false,
  onBack,
  allUsers = [],
  allShifts = [],
  assignedShifts = [],
  onAddMembers,
  onRemoveMember,
  onUpdateTeam,
  onAssignShift,
  onRemoveShift,
}: TeamDetailViewProps) {
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignShiftModal, setShowAssignShiftModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUsersToAdd, setSelectedUsersToAdd] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editForm, setEditForm] = useState<UpdateTeamInput>({});
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [selectedShiftId, setSelectedShiftId] = useState<number | null>(null);
  const [shiftStartDate, setShiftStartDate] = useState('');
  const [shiftEndDate, setShiftEndDate] = useState('');
  const [shiftSearchTerm, setShiftSearchTerm] = useState('');
  const [isAssignShiftSubmitting, setIsAssignShiftSubmitting] = useState(false);
  const [removingShiftKey, setRemovingShiftKey] = useState<string | null>(null);
  // When removing the current team lead, prompt for a replacement before removing.
  const [leadReassign, setLeadReassign] = useState<{ leadUserId: number } | null>(null);
  const [newLeadId, setNewLeadId] = useState<number | null>(null);
  const [isReassignSubmitting, setIsReassignSubmitting] = useState(false);

  const existingUserIds = new Set((team?.members ?? []).map((m) => String(m.userId)));
  const availableUsers = allUsers.filter((u) => !existingUserIds.has(u.id));
  const filteredAvailableUsers = availableUsers.filter(
    (user) =>
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.team.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const toggleUserSelection = (userId: string) => {
    if (selectedUsersToAdd.includes(userId)) {
      setSelectedUsersToAdd(selectedUsersToAdd.filter((id) => id !== userId));
    } else {
      setSelectedUsersToAdd([...selectedUsersToAdd, userId]);
    }
  };

  const handleAddSelectedMembers = async () => {
    if (!selectedUsersToAdd.length) return;
    setIsSubmitting(true);
    try {
      await onAddMembers?.(selectedUsersToAdd.map(Number));
      setShowAddMemberModal(false);
      setSelectedUsersToAdd([]);
      setSearchTerm('');
      showSuccessToast('Members Added', `${selectedUsersToAdd.length} member(s) added to team.`);
    } catch (error: any) {
      showErrorToast('Failed to Add Members', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (userId: number) => {
    // Removing the current team lead requires choosing a replacement first.
    if (team?.teamLeadId === userId) {
      const others = (team?.members ?? []).filter((m) => m.userId !== userId);
      if (others.length === 0) {
        showErrorToast(
          'Cannot Remove Team Leader',
          'Add another member before removing the last team leader.',
        );
        return;
      }
      setNewLeadId(null);
      setLeadReassign({ leadUserId: userId });
      return;
    }

    try {
      await onRemoveMember?.(userId);
      showSuccessToast('Member Removed', 'Team member removed successfully.');
    } catch (error: any) {
      showErrorToast('Failed to Remove Member', error?.message || 'An unexpected error occurred.');
    }
  };

  const handleConfirmReassignAndRemove = async () => {
    if (!leadReassign || !newLeadId) return;
    setIsReassignSubmitting(true);
    try {
      await onRemoveMember?.(leadReassign.leadUserId, newLeadId);
      setLeadReassign(null);
      setNewLeadId(null);
      showSuccessToast('Team Leader Updated', 'New team leader assigned and member removed.');
    } catch (error: any) {
      showErrorToast('Failed to Remove Member', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsReassignSubmitting(false);
    }
  };

  const handleCloseAddModal = () => {
    setShowAddMemberModal(false);
    setSelectedUsersToAdd([]);
    setSearchTerm('');
  };

  const openEditModal = () => {
    setEditForm({
      name: team?.name ?? '',
      description: team?.description ?? undefined,
      maxCapacity: team?.maxCapacity ?? undefined,
      teamLeadId: team?.teamLeadId ?? null,
    });
    setShowEditModal(true);
  };

  const validateEditForm = (): boolean => {
    const errors: Record<string, string> = {};
    const name = editForm.name?.trim() ?? '';
    const description = editForm.description?.trim() ?? '';

    if (!name) {
      errors.name = 'Team name is required';
    } else if (name.length > 50) {
      errors.name = 'Team name must be 50 characters or less';
    } else if (!ALLOWED_TEXT_PATTERN.test(name)) {
      errors.name = TEXT_VALIDATION_MSG;
    }

    if (description && description.length > 50) {
      errors.description = 'Description must be 50 characters or less';
    } else if (description && !ALLOWED_TEXT_PATTERN.test(description)) {
      errors.description = TEXT_VALIDATION_MSG;
    }

    if (editForm.maxCapacity != null) {
      const cap = Number(editForm.maxCapacity);
      if (Number.isNaN(cap) || cap < 1 || cap > 50) {
        errors.capacity = 'Daily job capacity must be between 1 and 50';
      }
    }

    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async () => {
    if (!validateEditForm()) return;
    setIsEditSubmitting(true);
    try {
      await onUpdateTeam?.(editForm);
      setShowEditModal(false);
      showSuccessToast('Team Updated', 'Team details updated successfully.');
    } catch (error: any) {
      showErrorToast('Failed to Update Team', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const filteredShifts = allShifts.filter((s) =>
    s.title.toLowerCase().includes(shiftSearchTerm.toLowerCase()),
  );

  const handleAssignShiftSubmit = async () => {
    if (!selectedShiftId || !shiftStartDate || !shiftEndDate) return;
    setIsAssignShiftSubmitting(true);
    try {
      // The assign hook owns the result toast (success / partial / fully-conflicted),
      // since only it knows the per-member summary. Don't announce success here.
      await onAssignShift?.(selectedShiftId, shiftStartDate, shiftEndDate);
      setShowAssignShiftModal(false);
      setSelectedShiftId(null);
      setShiftStartDate('');
      setShiftEndDate('');
      setShiftSearchTerm('');
    } catch (error: any) {
      showErrorToast('Failed to Assign Shift', error?.message || 'An unexpected error occurred.');
    } finally {
      setIsAssignShiftSubmitting(false);
    }
  };

  const handleRemoveShift = async (shift: TeamShiftAssignment) => {
    const key = `${shift.shiftId}-${shift.startDate}-${shift.endDate}`;
    setRemovingShiftKey(key);
    try {
      await onRemoveShift?.(shift.shiftId, shift.startDate, shift.endDate);
      showSuccessToast('Shift Removed', 'Shift assignment removed from this team.');
    } catch (error: any) {
      showErrorToast('Failed to Remove Shift', error?.message || 'An unexpected error occurred.');
    } finally {
      setRemovingShiftKey(null);
    }
  };

  const formatDate = (value: string) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase();
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Team not found</p>
          <Button variant="outline" className="mt-4" onClick={onBack}>
            Back to Teams
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="h-full flex flex-col overflow-hidden">
        <PageHeader
          title={team.name}
          subtitle={team.description ?? ''}
          icon={<UsersIcon className="w-7 h-7 text-orange-600 dark:text-orange-400" />}
          breadcrumbs={[
            { label: 'Teams', onClick: onBack },
            { label: team.name, current: true },
          ]}
          actions={
            <div className="flex gap-2">
              <Button variant="outline" onClick={openEditModal}>
                <Pencil className="w-4 h-4 mr-2" />
                Edit Team
              </Button>
              <Button variant="outline" onClick={() => setShowAssignShiftModal(true)}>
                <Clock className="w-4 h-4 mr-2" />
                Assign Shift
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={() => setShowAddMemberModal(true)}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Add Members
              </Button>
            </div>
          }
        />

        <div className="flex-1 overflow-y-auto">
          {/* Team Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Team Members</div>
              <div className="text-2xl font-bold text-foreground">{team.memberCount}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Daily Job Capacity</div>
              <div className="text-2xl font-bold text-foreground">{team.maxCapacity ?? '—'}</div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Active Jobs</div>
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                {team.activeJobs}
              </div>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="text-sm text-muted-foreground mb-1">Utilisation</div>
              <div
                className={`text-2xl font-bold ${
                  team.utilizationRate >= 80
                    ? 'text-red-600 dark:text-red-400'
                    : team.utilizationRate >= 60
                      ? 'text-yellow-600 dark:text-yellow-400'
                      : 'text-green-600 dark:text-green-400'
                }`}
              >
                {team.utilizationRate}%
              </div>
            </div>
          </div>

          {/* Assigned Shifts List */}
          <div className="bg-card border border-border rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Assigned Shifts</h3>
              <Badge
                variant="outline"
                className="bg-orange-50 dark:bg-orange-950/30 text-orange-600 border-orange-200 dark:border-orange-800"
              >
                {assignedShifts.length} {assignedShifts.length === 1 ? 'shift' : 'shifts'}
              </Badge>
            </div>

            <div className="space-y-3">
              {assignedShifts.map((shift) => {
                const key = `${shift.shiftId}-${shift.startDate}-${shift.endDate}`;
                const isRemoving = removingShiftKey === key;
                return (
                  <div
                    key={key}
                    className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors"
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div
                        className="w-3 h-10 rounded flex-shrink-0"
                        style={{ backgroundColor: shift.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground">
                          {shift.title}
                          {shift.code ? (
                            <span className="text-muted-foreground font-normal">
                              {' '}
                              ({shift.code})
                            </span>
                          ) : null}
                        </div>
                        <div className="text-sm text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3.5 h-3.5" />
                          {shift.startTime} - {shift.endTime}
                        </div>
                      </div>
                      <div className="hidden sm:block text-sm text-muted-foreground whitespace-nowrap">
                        {formatDate(shift.startDate)} – {formatDate(shift.endDate)}
                      </div>
                      <Badge variant="outline" className="text-xs whitespace-nowrap">
                        {shift.memberCount} {shift.memberCount === 1 ? 'member' : 'members'}
                      </Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                      onClick={() => handleRemoveShift(shift)}
                      disabled={isRemoving}
                      aria-label={`Remove ${shift.title} shift`}
                    >
                      {isRemoving ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                );
              })}

              {assignedShifts.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No shifts assigned to this team yet</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setShowAssignShiftModal(true)}
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    Assign First Shift
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Team Members List */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Team Members</h3>
              <Badge
                variant="outline"
                className="bg-blue-50 dark:bg-blue-950/30 text-blue-600 border-blue-200 dark:border-blue-800"
              >
                {team.memberCount} {team.memberCount === 1 ? 'member' : 'members'}
              </Badge>
            </div>

            <div className="space-y-3">
              {team.members.map((member) => (
                <div
                  key={member.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border border-border hover:bg-muted/70 transition-colors"
                >
                  <div className="flex items-center gap-4 flex-1">
                    <Avatar className="w-10 h-10 bg-orange-600 text-white">
                      <AvatarFallback className="bg-orange-600 text-white text-sm">
                        {getInitials(member.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground flex items-center gap-2">
                        {member.name}
                        {team.teamLeadId === member.userId && (
                          <Badge className="bg-orange-600 text-white border-0 text-xs">Lead</Badge>
                        )}
                      </div>
                      <div className="text-sm text-muted-foreground">{member.role ?? 'Member'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge
                        variant="outline"
                        className={
                          member.taskId !== null
                            ? 'bg-green-50 text-green-700 border-green-600 dark:bg-green-950/30'
                            : 'bg-slate-50 text-slate-700 border-slate-400 dark:bg-slate-900/30'
                        }
                      >
                        {member.taskId !== null ? 'active' : 'available'}
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="ml-4 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
                    onClick={() => handleRemoveMember(member.userId)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}

              {team.members.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <UsersIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No members in this team yet</p>
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setShowAddMemberModal(true)}
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add First Member
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit Team Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Edit Team</h2>
                <p className="text-sm text-muted-foreground">Update team details</p>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Team Name <span className="text-red-500">*</span>
                </label>
                <Input
                  value={editForm.name ?? ''}
                  onChange={(e) => {
                    setEditForm((f) => ({ ...f, name: e.target.value }));
                    if (editErrors.name) setEditErrors((prev) => ({ ...prev, name: '' }));
                  }}
                  maxLength={50}
                  placeholder="Enter team name"
                />
                {editErrors.name && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">{editErrors.name}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Description
                </label>
                <Input
                  value={editForm.description ?? ''}
                  onChange={(e) => {
                    setEditForm((f) => ({ ...f, description: e.target.value || undefined }));
                    if (editErrors.description)
                      setEditErrors((prev) => ({ ...prev, description: '' }));
                  }}
                  maxLength={50}
                  placeholder="Optional description"
                />
                {editErrors.description && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {editErrors.description}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Daily Job Capacity
                </label>
                <Input
                  type="number"
                  min={1}
                  max={50}
                  value={editForm.maxCapacity ?? ''}
                  onChange={(e) => {
                    setEditForm((f) => ({
                      ...f,
                      maxCapacity: e.target.value ? Number(e.target.value) : undefined,
                    }));
                    if (editErrors.capacity) setEditErrors((prev) => ({ ...prev, capacity: '' }));
                  }}
                  placeholder="Max jobs per day (1–50)"
                />
                {editErrors.capacity && (
                  <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                    {editErrors.capacity}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Team Lead
                </label>
                <Select
                  value={
                    editForm.teamLeadId !== undefined && editForm.teamLeadId !== null
                      ? String(editForm.teamLeadId)
                      : '__none__'
                  }
                  onValueChange={(value) =>
                    setEditForm((f) => ({
                      ...f,
                      teamLeadId: value === '__none__' ? null : Number(value),
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="No team lead" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">No team lead</SelectItem>
                    {team.members.map((m) => (
                      <SelectItem key={m.id} value={String(m.userId)}>
                        {m.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Choose a lead from the current members
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button variant="outline" onClick={() => setShowEditModal(false)}>
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleEditSubmit}
                disabled={!editForm.name?.trim() || isEditSubmitting}
              >
                {isEditSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reassign Team Leader Modal — shown when removing the current lead */}
      {leadReassign && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Select New Team Leader</h2>
                <p className="text-sm text-muted-foreground">
                  This member is the team leader. Choose a replacement before removing them.
                </p>
              </div>
              <button
                onClick={() => {
                  setLeadReassign(null);
                  setNewLeadId(null);
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-2 max-h-[50vh] overflow-y-auto">
              {team.members
                .filter((m) => m.userId !== leadReassign.leadUserId)
                .map((m) => (
                  <div
                    key={m.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                      newLeadId === m.userId
                        ? 'border-orange-600 bg-orange-50 dark:bg-orange-950/20'
                        : 'border-border hover:bg-muted/50'
                    }`}
                    onClick={() => setNewLeadId(m.userId)}
                  >
                    <Avatar className="w-9 h-9 bg-blue-600 text-white">
                      <AvatarFallback className="bg-blue-600 text-white text-sm">
                        {getInitials(m.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-foreground text-sm">{m.name}</div>
                      <div className="text-xs text-muted-foreground">{m.role ?? 'Member'}</div>
                    </div>
                    {newLeadId === m.userId && (
                      <Badge className="bg-orange-600 text-white border-0 text-xs">
                        New Leader
                      </Badge>
                    )}
                  </div>
                ))}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setLeadReassign(null);
                  setNewLeadId(null);
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleConfirmReassignAndRemove}
                disabled={!newLeadId || isReassignSubmitting}
              >
                {isReassignSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Reassign &amp; Remove
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Members Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Add Team Members</h2>
                <p className="text-sm text-muted-foreground">
                  Select multiple users to add to the team
                </p>
              </div>
              <button
                onClick={handleCloseAddModal}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search users by name, email, or team..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
              {selectedUsersToAdd.length > 0 && (
                <div className="mt-2 text-sm text-muted-foreground">
                  {selectedUsersToAdd.length} user{selectedUsersToAdd.length !== 1 ? 's' : ''}{' '}
                  selected
                </div>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="space-y-2">
                {filteredAvailableUsers.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No available users found</p>
                  </div>
                )}
                {filteredAvailableUsers.map((user) => (
                  <div
                    key={user.id}
                    className={`flex items-center justify-between p-3 rounded-lg border transition-all cursor-pointer ${
                      selectedUsersToAdd.includes(user.id)
                        ? 'border-orange-600 bg-orange-50 dark:bg-orange-950/20'
                        : 'border-border hover:bg-muted/50'
                    }`}
                    onClick={() => toggleUserSelection(user.id)}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar
                        className={`w-9 h-9 ${selectedUsersToAdd.includes(user.id) ? 'bg-orange-600' : 'bg-blue-600'} text-white`}
                      >
                        <AvatarFallback
                          className={`${selectedUsersToAdd.includes(user.id) ? 'bg-orange-600' : 'bg-blue-600'} text-white text-sm`}
                        >
                          {getInitials(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-foreground text-sm">{user.name}</div>
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedUsersToAdd.includes(user.id) && (
                        <Badge className="bg-orange-600 text-white border-0">Selected</Badge>
                      )}
                      <Badge variant="outline" className="text-xs">
                        {user.role}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                {selectedUsersToAdd.length > 0 ? (
                  <span className="font-medium text-foreground">
                    {selectedUsersToAdd.length} user{selectedUsersToAdd.length !== 1 ? 's' : ''}{' '}
                    ready to add
                  </span>
                ) : (
                  'Select users to add to the team'
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCloseAddModal}>
                  Cancel
                </Button>
                <Button
                  className="bg-orange-600 hover:bg-orange-700 text-white"
                  onClick={handleAddSelectedMembers}
                  disabled={selectedUsersToAdd.length === 0 || isSubmitting}
                >
                  {isSubmitting ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2" />
                  )}
                  Add {selectedUsersToAdd.length > 0 ? `${selectedUsersToAdd.length} ` : ''}
                  Member{selectedUsersToAdd.length !== 1 ? 's' : ''}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Shift Modal */}
      {showAssignShiftModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg border border-border shadow-xl max-w-lg w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Assign Shift to Team</h2>
                <p className="text-sm text-muted-foreground">
                  Select a shift and date range for {team?.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAssignShiftModal(false);
                  setSelectedShiftId(null);
                  setShiftStartDate('');
                  setShiftEndDate('');
                  setShiftSearchTerm('');
                }}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-1.5 block">
                  Select Shift *
                </label>
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search shifts..."
                    value={shiftSearchTerm}
                    onChange={(e) => setShiftSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1 border border-border rounded-lg p-2">
                  {filteredShifts.map((shift) => (
                    <div
                      key={shift.id}
                      className={`flex items-center gap-3 p-2 rounded cursor-pointer transition-all ${
                        selectedShiftId === shift.id
                          ? 'bg-orange-50 dark:bg-orange-950/20 border border-orange-600'
                          : 'hover:bg-muted/50 border border-transparent'
                      }`}
                      onClick={() => setSelectedShiftId(shift.id)}
                    >
                      <div
                        className="w-4 h-4 rounded flex-shrink-0"
                        style={{ backgroundColor: shift.color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-foreground">{shift.title}</div>
                        <div className="text-xs text-muted-foreground">
                          {shift.startTime} - {shift.endTime}
                          {shift.code ? ` (${shift.code})` : ''}
                        </div>
                      </div>
                      {selectedShiftId === shift.id && (
                        <Badge className="bg-orange-600 text-white border-0 text-xs">
                          Selected
                        </Badge>
                      )}
                    </div>
                  ))}
                  {filteredShifts.length === 0 && (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No shifts found
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    Start Date *
                  </label>
                  <Input
                    type="date"
                    value={shiftStartDate}
                    onChange={(e) => setShiftStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground mb-1.5 block">
                    End Date *
                  </label>
                  <Input
                    type="date"
                    value={shiftEndDate}
                    onChange={(e) => setShiftEndDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAssignShiftModal(false);
                  setSelectedShiftId(null);
                  setShiftStartDate('');
                  setShiftEndDate('');
                  setShiftSearchTerm('');
                }}
              >
                Cancel
              </Button>
              <Button
                className="bg-orange-600 hover:bg-orange-700 text-white"
                onClick={handleAssignShiftSubmit}
                disabled={
                  !selectedShiftId || !shiftStartDate || !shiftEndDate || isAssignShiftSubmitting
                }
              >
                {isAssignShiftSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Assign Shift
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
